import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';
import { applyWarehouseFilter, warehouseParamsSerializer, appendWarehouseParams } from '../utils/warehouseParams';
import { registerClearable } from '../utils/cacheRegistry';

// Axios instance for sales API
const salesAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: warehouseParamsSerializer,
});

// Attach auth token if available
salesAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;
    return applyWarehouseFilter(config);
  },
  (error) => Promise.reject(error)
);

// Redirect on auth failure
salesAPI.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Caching helpers (memory + sessionStorage)
const CACHE_PREFIX = 'erp_sales_cache_v1';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

const memoryCache = new Map();

function cacheKey(key) {
  return `${CACHE_PREFIX}::${key}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires) return null;
    if (Date.now() > parsed.expires) {
      sessionStorage.removeItem(cacheKey(key));
      return null;
    }
    return parsed.value;
  } catch (e) {
    return null;
  }
}

function writeCache(key, value, ttl = DEFAULT_TTL_MS) {
  try {
    sessionStorage.setItem(cacheKey(key), JSON.stringify({ value, expires: Date.now() + ttl }));
  } catch (e) { }
}

function readMemoryCache(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expires) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}

function writeMemoryCache(key, value, ttl = DEFAULT_TTL_MS) {
  memoryCache.set(key, { value, expires: Date.now() + ttl });
}

// API Methods
const salesService = {
  // Get sales daybook with date range
  async getSalesDaybook(fromDate, toDate, params = {}) {
    const { force = false, skip = 0, limit = 20, search, ...extra } = params;
    
    // Sort extra keys for stable cache key
    const extraStr = Object.keys(extra).sort().map(k => `${k}=${extra[k]}`).join('&');
    const key = `sales_daybook_${fromDate}_${toDate}_${skip}_${limit}_${search || ''}_${extraStr}`;

    if (!force) {
      const cached = readCache(key) || readMemoryCache(key);
      if (cached) return { success: true, data: cached };
    }

    try {
      const query = new URLSearchParams({ from_date: fromDate, to_date: toDate, skip, limit });
      if (search) query.append('search', search);
      
      // Append any extra filters provided
      Object.entries(extra).forEach(([k, v]) => {
        if (v !== undefined && v !== null) query.append(k, v);
      });
      
      appendWarehouseParams(query);

      const response = await salesAPI.get(`/sales-daybook?${query.toString()}`);
      const data = response.data;

      writeCache(key, data);
      writeMemoryCache(key, data);

      return { success: true, data };
    } catch (error) {
      console.error('Failed to fetch sales daybook:', error);
      return { success: false, error: extractApiError(error) };
    }
  },

  // Download sales daybook export (using delivery challans export)
  async downloadSalesDaybook(fromDate, toDate, params = {}) {
    try {
      const query = new URLSearchParams({ 
        from_date: fromDate, 
        to_date: toDate,
        format: 'csv'
      });
      
      // Append any extra filters provided
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) query.set(k, v);
      });
      
      appendWarehouseParams(query);

      const response = await salesAPI.get(`/delivery-challans/export/download?${query.toString()}`, {
        responseType: 'blob',
      });
      
      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      console.error('Failed to download sales daybook export:', error);
      return { success: false, error: extractApiError(error) };
    }
  },

  /**
   * Drop every cached entry for this service.
   *
   * Deleting an order elsewhere (e.g. the Proforma Invoice screen's bulk delete, which
   * goes through ordersService) leaves these lists holding rows the server no longer
   * has. Without this the next fetch is served from sessionStorage for the rest of the
   * TTL and the deleted records reappear.
   */
  clearCache() {
    try {
      memoryCache.clear();
    } catch (e) { /* non-fatal */ }
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k);
      }
    } catch (e) { /* sessionStorage unavailable (private mode / SSR) */ }
  },
};

// Cleared on logout together with the other services (policy C16).
registerClearable(() => salesService.clearCache());

export default salesService;